import { SortingState } from "@tanstack/react-table";
import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import Colors from "Constants/Color";
import usePermission from "CustomHooks/usePermission";
import { api } from "helpers/api_url";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { fetchInStock } from "slices/Warehouse/ProjectWarehouse/InStock/thunk";

type PendingStockTransferProps = {
  outloading?: boolean;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  columnFilters: any[];
  filterValue: any[];
  search: string;
  searchInput: string;
  warehouseIdSearch: string;
  sortField: string;
  sortDirection: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<any[]>>;
  setFilterValue: React.Dispatch<React.SetStateAction<any[]>>;
  setWarehouseIdSearch: React.Dispatch<React.SetStateAction<string>>;
};

const InStock: React.FC<PendingStockTransferProps> = ({
  outloading,
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  searchInput,
  warehouseIdSearch,
  sortField,
  sortDirection,
  setSearch,
  setSearchInput,
  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
  setWarehouseIdSearch,
}) => {
  document.title = "In Stock";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const selectInStock = createSelector(
    (state: any) => state.InStock,
    (item) => ({
      list: item?.list || [],
      rowCount: item?.rowCount || 0,
      loading: item?.loading || false,
      error: item?.error || null,
    })
  );

  const { list, rowCount, loading } = useSelector(selectInStock);

  // Use custom hook to fetch and access permissions
  const { canUpdate } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.item_inward
  );

  //*// Need to change this with fetching pending Stock Transfer data
  useEffect(() => {
    if (warehouseIdSearch) {
      dispatch(
        fetchInStock({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? (sortDirection as "asc" | "desc") : "",
          search,
          filters: filterValue,
          warehouse_id: warehouseIdSearch,
        })
      );
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
    warehouseIdSearch,
  ]);
  //*//

  //*// Last Column Action Button
  const DynamicAction = useMemo(() => {
    if (!canUpdate) return [];
    return [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <ul className="d-flex gap-2 list-unstyled mb-0">
            <li>
              <button
                className="btn btn-sm"
                style={{
                  backgroundColor: Colors?.themeBgColor,
                  color: "#FFF",
                }}
                onClick={() =>
                  navigate(
                    `/warehouse-overview/view-warehouse-in-stock/${row.original.master_item_id}/${warehouseIdSearch}`
                  )
                }
              >
                View
              </button>
            </li>
          </ul>
        ),
      },
    ];
  }, [canUpdate, navigate, warehouseIdSearch]);

  //*// Listing tanstack table columns
  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row, table }: { row: any; table: any }) => {
          const pageIndex = table.getState().pagination.pageIndex;
          const pageSize = table.getState().pagination.pageSize;
          const rowIndex = row.index;
          return pageIndex * pageSize + rowIndex + 1;
        },
      },

      {
        header: "Item Name",
        accessorKey: "item_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.in_stock.dropdown}/item_name?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original?.item_name || "-";
        },
      },
      {
        header: "Item Code",
        accessorKey: "item_code",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.in_stock.dropdown}/item_code?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original?.item_code || "-";
        },
      },

      {
        header: "Category",
        accessorKey: "category_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.in_stock.dropdown}/category_name?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original.category_name || "-";
        },
      },
      {
        header: "Sub Category",
        accessorKey: "sub_category_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.in_stock.dropdown}/sub_category_name?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original?.sub_category_name || "-";
        },
      },
      {
        header: "Unit",
        accessorKey: "unit_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.in_stock.dropdown}/unit_name?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original.unit_name || "-";
        },
      },
      {
        header: "Total Quantity",
        accessorKey: "total_quantity",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.in_stock.dropdown}/total_quantity?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original.total_quantity;
        },
      },
      // {
      //   header: "Created By/Date",
      //   accessorKey: "created_at",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "date" as const,
      //   cell: ({ row }: { row: any }) => {
      //     const name = row.original.created_by_user ?? " - ";
      //     const date = row.original.created_at
      //       ? new Date(row.original.created_at).toISOString().split("T")[0]
      //       : " - ";
      //     return `${name} - ${date}`;
      //   },
      // },

      ...DynamicAction,
    ],
    [warehouseIdSearch, DynamicAction]
  );

  return (
    <Row id="invoiceList">
      <Col lg={12}>
        <Card>
          <Card.Body className="mt-3">
            <LoaderErrorWrapper loading={loading}>
              <TableContainer2
                columns={columns}
                data={list || []}
                totalRecords={rowCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setFilterValue}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder="Search..."
                manualSorting={true}
              />
            </LoaderErrorWrapper>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default InStock;
